import React, { useId } from 'react';
import { useTranslation } from 'localization';
import './SyndicationRevokedNotificationCardMolecule.scss';

export interface SyndicationRevokedNotificationMetadata {
  revokedItemName: string;
  revokedItemType: string;
  sourceChannelName: string;
  targetChannelName: string;
  targetChannelId?: string;
}

export interface SyndicationRevokedNotificationCardMoleculeProps {
  metadata: SyndicationRevokedNotificationMetadata;
  onNavigate?: (targetChannelId: string | undefined) => void;
  className?: string;
}

const SyndicationRevokedNotificationCardMolecule: React.FC<
  SyndicationRevokedNotificationCardMoleculeProps
> = ({ metadata, onNavigate, className = '' }) => {
  const { t } = useTranslation();
  const {
    revokedItemName,
    revokedItemType,
    sourceChannelName,
    targetChannelName,
    targetChannelId,
  } = metadata;

  const titleId = useId();
  const isClickable = Boolean(onNavigate);

  const handleActivate = () => {
    if (onNavigate) {
      onNavigate(targetChannelId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate();
    }
  };

  const classes = [
    'marketplace-syndication-revoked-notification-card',
    isClickable ? 'marketplace-syndication-revoked-notification-card--clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role={isClickable ? 'button' : 'status'}
      tabIndex={isClickable ? 0 : undefined}
      aria-labelledby={titleId}
      onClick={isClickable ? handleActivate : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      <i
        className="pi pi-exclamation-triangle marketplace-syndication-revoked-notification-card__icon"
        aria-hidden="true"
      />
      <div className="marketplace-syndication-revoked-notification-card__content">
        <span
          id={titleId}
          className="marketplace-syndication-revoked-notification-card__title"
        >
          {t('notifications.syndicationRevoked.title')}
        </span>
        <p className="marketplace-syndication-revoked-notification-card__body">
          {t('notifications.syndicationRevoked.body', {
            itemName: revokedItemName,
            itemType: revokedItemType,
            sourceChannel: sourceChannelName,
            targetChannel: targetChannelName,
          })}
        </p>
      </div>
    </div>
  );
};

SyndicationRevokedNotificationCardMolecule.displayName =
  'SyndicationRevokedNotificationCardMolecule';

export default SyndicationRevokedNotificationCardMolecule;
